/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { ResizeHandle } from 'src/modules/labeling/components/ResizeHandle';
import { ShapeUIProps } from 'src/modules/labeling/types/ShapeUIProps';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import {
    LABEL_Z_INDEX,
    SHAPE_DEPTH_ONE_THICKNESS,
    SHAPE_HORIZONTAL_MARGIN,
    SHAPE_LEVEL_HEIGHT,
    SHAPE_LEVEL_HEIGHT_NO_NAME,
    SHAPE_THICKNESS
} from 'src/modules/labeling/utils/labelingConstants';
import { getTooltipTargetToken } from 'src/modules/labeling/utils/layout';

export type ShapeRendererProps = Omit<ShapeUIProps, 'label'> & {
    /**
     * Labeling view options.
     */
    viewOptions: ViewOptions;

    /**
     * Unique identifier for the tokens container.
     */
    containerId?: string;

    /**
     * A function that gets invoked whenever the shape gets clicked.
     */
    onClick: () => void;

    /**
     * An optional callback used for rendering a tooltip component.
     */
    onRenderTooltip?: (children: React.ReactNode, target: Target) => React.ReactNode;

    /**
     * A callback that gets invoked when a resize handle gets clicked.
     */
    onHandlesKeydown: (knob: 'start' | 'end') => void;

    /**
     * Whether resizing the label is allowed or not.
     */
    disableResize: boolean;

    /**
     * Shape's color.
     */
    color: string;

    /**
     * Make visual tweaks to the labeler to make
     * it compatible with char tokenization.
     */
    useCharTokenization: boolean;
};

const { getClassName } = getStylistV2('Renderers');

const lineContainerClassName = getClassName('LineContainer', {
    position: 'absolute',
    left: 0,
    willChange: 'transform'
});

const lineTextContainerClassName = getClassName('LineTextContainer', {
    top: 0,
    left: 0,
    right: 0,
    height: 13,
    display: 'flex',
    position: 'absolute',
    cursor: 'pointer'
});

const lineTextClassName = getClassName('LineText', theme => ({
    userSelect: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: theme.fontFamily,
    fontSize: 10
}));

const boxTextContainerClassName = getClassName('BoxTextContainer', {
    display: 'flex',
    height: 13,
    left: -1,
    position: 'absolute',
    right: 0,
    top: -15,
    cursor: 'pointer'
});

const boxTextClassName = getClassName('BoxText', {
    fontStyle: 'italic'
});

/**
 * Shape renderer component used for displaying labels.
 */
export const LineRenderer = React.memo(
    React.forwardRef((props: ShapeRendererProps, ref: React.MutableRefObject<HTMLSpanElement>) => {
        const {
            name,
            depth = 1,
            start,
            end,
            renderingLevel,
            className,
            hasBorderStart,
            hasBorderEnd,
            line,
            viewOptions: { hideNames, hasLabelBorders },
            tokenHeight: height,
            onClick,
            onRenderTooltip,
            onHandlesKeydown,
            disableResize,
            containerId,
            renderedAs,
            associatedTokenIndex,
            top: tokenTop,
            color,
            useCharTokenization
        } = props;

        const lineTextContainerId = React.useRef(getId());

        const clickAreaHeight = 14;
        const levelHeight = hideNames ? SHAPE_LEVEL_HEIGHT_NO_NAME : SHAPE_LEVEL_HEIGHT;
        const top = tokenTop + height + (renderingLevel - 1) * levelHeight + 1;
        const offset = useCharTokenization ? 0 : SHAPE_HORIZONTAL_MARGIN;

        const content = !hideNames && (
            <span className={lineTextContainerClassName} id={lineTextContainerId.current}>
                <span className={lineTextClassName} style={{ color }}>
                    {hasBorderStart && name}
                </span>
            </span>
        );

        return (
            <span
                ref={ref}
                role="button"
                data-line={line}
                onClick={onClick}
                className={`${lineContainerClassName} ${className}`}
                style={{
                    top,
                    transform: `translateX(${start + offset}px)`,
                    width: end - start - offset * 2,
                    borderTop: `${depth === 1 ? SHAPE_DEPTH_ONE_THICKNESS : SHAPE_THICKNESS}px ${
                        renderedAs === 'dashedLine' ? 'dashed' : 'solid'
                    } ${color}`,
                    height: clickAreaHeight,
                    zIndex: LABEL_Z_INDEX
                }}>
                {onRenderTooltip ? onRenderTooltip(content, getTooltipTargetToken(containerId, associatedTokenIndex)) : content}
                {hasLabelBorders && hasBorderStart && (
                    <ResizeHandle
                        onMouseDown={() => onHandlesKeydown('start')}
                        customProps={{ variant: depth === 1 ? 'large' : 'small', color, position: 'start', disabled: disableResize }}
                    />
                )}
                {hasLabelBorders && hasBorderEnd && (
                    <ResizeHandle
                        onMouseDown={() => onHandlesKeydown('end')}
                        customProps={{ variant: depth === 1 ? 'large' : 'small', color, position: 'end', disabled: disableResize }}
                    />
                )}
            </span>
        );
    })
);

LineRenderer.displayName = 'LineRenderer';

/**
 * Shape renderer component used for displaying predictions.
 */
export const BoxRenderer = React.memo(
    React.forwardRef((props: ShapeRendererProps, ref: React.MutableRefObject<HTMLSpanElement>) => {
        const {
            name,
            renderingLevel,
            depth = 1,
            start,
            end,
            className,
            tokenHeight,
            top: tokenTop,
            hasBorderStart,
            hasBorderEnd,
            line,
            viewOptions: { hideNames },
            onClick,
            onRenderTooltip,
            onHandlesKeydown,
            disableResize,
            color,
            useCharTokenization
        } = props;

        const boxTextContainerId = React.useRef(getId());

        const borderStyle = `1px dotted ${color}`;

        const levelHeight = hideNames ? SHAPE_LEVEL_HEIGHT_NO_NAME : SHAPE_LEVEL_HEIGHT;
        const top = tokenTop - (renderingLevel - 1) * levelHeight;
        const height = tokenHeight + (renderingLevel - 1) * levelHeight * 2;

        const content = !hideNames && (
            <span
                id={boxTextContainerId.current}
                role="button"
                className={boxTextContainerClassName}
                style={{ zIndex: depth }}
                onClick={onClick}>
                <span className={`${lineTextClassName} ${boxTextClassName}`} style={{ color }} color={color}>
                    {!hideNames && hasBorderStart && name}
                </span>
            </span>
        );
        const resizeHandleProps = {
            variant: depth === 1 ? 'large' : 'small',
            color,
            mode: 'prediction',
            cursor: 'diagonal',
            hidden: true,
            disabled: disableResize
        } as const;

        return (
            <span
                ref={ref}
                data-line={line}
                className={`${lineContainerClassName} ${className}`}
                style={{
                    top,
                    height,
                    transform: `translateX(${start + (useCharTokenization ? 0 : SHAPE_HORIZONTAL_MARGIN)}px)`,
                    width: end - start - (useCharTokenization ? 0 : SHAPE_HORIZONTAL_MARGIN * 2),
                    borderTop: borderStyle,
                    borderBottom: borderStyle,
                    borderLeft: hasBorderStart && borderStyle,
                    borderRight: hasBorderEnd && borderStyle
                }}>
                {hasBorderStart && (
                    <ResizeHandle
                        onMouseDown={() => onHandlesKeydown('start')}
                        customProps={{
                            ...resizeHandleProps,
                            position: 'start'
                        }}
                    />
                )}
                {hasBorderEnd && (
                    <ResizeHandle
                        onMouseDown={() => onHandlesKeydown('end')}
                        customProps={{
                            ...resizeHandleProps,
                            position: 'end'
                        }}
                    />
                )}
                {onRenderTooltip ? onRenderTooltip(content, `#${boxTextContainerId.current}`) : content}
            </span>
        );
    })
);

BoxRenderer.displayName = 'BoxRenderer';
